import type {VersionLabel} from './types';

// Dashboard-friendly ms → "1m 18s" / "55s". Distinct from utils/transform's moment-based
// `formatDuration` (which yields clock-style mm:ss); this one is the compact analytics style with
// a per-widget empty label ("0s", "<1s", "—") for missing/sub-second values.
const formatStageDuration = (ms: number, emptyLabel = '0s'): string => {
    if (!ms || ms < 1000) {
        return emptyLabel;
    }
    const sec = Math.round(ms / 1000);
    if (sec < 60) {
        return `${sec}s`;
    }
    const min = Math.floor(sec / 60);
    const remainder = sec % 60;
    return remainder === 0 ? `${min}m` : `${min}m ${remainder}s`;
};

// Resolve step names + form-field labels from the version snapshots. Steps/fields are keyed by
// stable id; when an id appears in several versions, the latest one (by lastUsedAt) wins.
const buildVersionLabelMaps = (versions: VersionLabel[]): {
    stepNames: Map<string, string>;
    fieldLabels: Map<string, string>;
} => {
    const sorted = [...versions].sort(
        (a, b) => new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
    );
    const stepNames = new Map<string, string>();
    const fieldLabels = new Map<string, string>();
    for (const version of sorted) {
        for (const step of version.steps) {
            if (!stepNames.has(step.stepId)) {
                stepNames.set(step.stepId, step.name);
            }
            for (const field of step.fields ?? []) {
                if (!fieldLabels.has(field.fieldId)) {
                    fieldLabels.set(field.fieldId, field.label);
                }
            }
        }
    }
    return {stepNames, fieldLabels};
};

export const preSessionFlowAnalyticsUtils = {
    formatStageDuration,
    buildVersionLabelMaps,
};
